import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getErrorMessage } from '../api/errors';
import { couponService } from '../services/coupons/couponService';
import type { AppliedCoupon } from '../types/coupon';

type CouponContextValue = {
  appliedCoupon: AppliedCoupon | null;
  isValidating: boolean;
  error: string | null;
  applyCoupon: (code: string, subtotal: number) => Promise<void>;
  removeCoupon: () => void;
  clearError: () => void;
};

const CouponContext = createContext<CouponContextValue | null>(null);

export function CouponProvider({ children }: { children: ReactNode }) {
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyCoupon = useCallback(async (code: string, subtotal: number) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Kupon kodu girin.');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await couponService.validateCoupon({ code: trimmed, subtotal });
      setAppliedCoupon({
        code: result.code,
        discountAmount: result.discountAmount,
        description: result.description,
      });
    } catch (err) {
      setAppliedCoupon(null);
      setError(getErrorMessage(err, 'Kupon doğrulanamadı.'));
      throw err;
    } finally {
      setIsValidating(false);
    }
  }, []);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({
      appliedCoupon,
      isValidating,
      error,
      applyCoupon,
      removeCoupon,
      clearError,
    }),
    [appliedCoupon, isValidating, error, applyCoupon, removeCoupon, clearError],
  );

  return <CouponContext.Provider value={value}>{children}</CouponContext.Provider>;
}

export function useCoupon() {
  const context = useContext(CouponContext);
  if (!context) {
    throw new Error('useCoupon must be used within CouponProvider');
  }
  return context;
}
